import React, { useEffect, useRef, useState } from 'react';
import { Dispatch, RootState } from '../../../../store/store';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getControllerPowerShareStatus,
  powerShareAmpsAction,
  powerShareBoolActions,
} from './powerShareActions';
import { Form, Formik } from 'formik';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import SettingsItem from '../../../../components/SettingItem/SettingItem';
import SelectOption, { SelectedValue } from '../../../../components/SelectOption/SelectOption';
import { disableOption, enableOption, outputChangeOptions } from './constants';
import { IControllerSettings } from '../../../../types/controller-types';
import { ChangeControllerSettingsAction } from '../../../../enums/action';
import { SingleValueForm } from '../../Components/SingleValueForm';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
type UpdateOptionStateParams = { option: SelectedValue; action: ChangeControllerSettingsAction };

const PowerShareView: React.FC<ComponentProps> = ({ settings, getSettings, reset }) => {
  const { id } = useParams();
  const [powerShareEnableOpt, setPowerShareEnableOpt] =
    useState<Record<ChangeControllerSettingsAction, SelectedValue>>(null);

  const updateOptionState = (data: UpdateOptionStateParams) => {
    setPowerShareEnableOpt((state) => ({ ...state, [data.action]: data.option }));
  };

  const updateOptionsState = (options: UpdateOptionStateParams[]) => {
    const statesUpdate = {};

    options.forEach((data) => {
      statesUpdate[data.action] = data.option;
    });

    setPowerShareEnableOpt((state) => ({ ...state, ...statesUpdate }));
  };

  const onChangeOutputOption = (
    event: React.ChangeEvent<HTMLSelectElement>,
    action: ChangeControllerSettingsAction,
  ) => {
    const { value } = event.target;

    const selectedValue: SelectedValue = value as unknown as SelectedValue;

    updateOptionState({ option: selectedValue, action });
  };

  const isOptionSetted = useRef(false);

  const setDefaultOptions = (settings?: IControllerSettings) => {
    if (isOptionSetted.current || !settings) {
      return;
    }

    const powerShareOption = settings.is_power_share_enabled.value ? enableOption : disableOption;
    const allowFullPower = settings.is_allowed_to_run_at_100.value ? enableOption : disableOption;
    const runIfFaulted = settings.preheat_run_if_faulted.value ? enableOption : disableOption;

    const dataToUpdate: UpdateOptionStateParams[] = [
      {
        option: powerShareOption,
        action: ChangeControllerSettingsAction.POWER_SHARING_ENABLE,
      },
      { option: allowFullPower, action: ChangeControllerSettingsAction.FULL_POWER_ALLOWING },
      { option: runIfFaulted, action: ChangeControllerSettingsAction.PRE_HEAT_RUN_WITH_FAULT },
    ];

    updateOptionsState(dataToUpdate);

    isOptionSetted.current = true;
  };

  useEffect(() => {
    getSettings(id).then(({ controller_settings }) => setDefaultOptions(controller_settings));
    const intervalId = setInterval(() => getSettings(id), EXTENDED_INTERVAL_UPDATE_DATA);
    return () => clearInterval(intervalId);
  }, [id]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  if (!settings || !powerShareEnableOpt) {
    return (
      <div className="flex items-center justify-center">
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <div className="p-edit-controller-settings pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">Edit Controller Power Share</h3>
        </div>

        <SingleValueForm
          key={powerShareAmpsAction.actionName}
          type={powerShareAmpsAction.inputType}
          values={{
            [powerShareAmpsAction.actionField]: settings[powerShareAmpsAction.actionField]?.value,
          }}
          label={powerShareAmpsAction.labelText}
          currentValues={settings.power_share_current_limit}
          onSubmit={(val) => powerShareAmpsAction.onSubmit(id, val[powerShareAmpsAction.actionField])}
          validationSchema={powerShareAmpsAction?.validationSchema}
        />

        {powerShareBoolActions.map((action, idx) => {
          return (
            <SettingsItem
              key={idx}
              className="p-7"
              title={action?.labelText}
              value={getControllerPowerShareStatus(settings[action.actionField].value)}
              updatedAt={new Date(settings[action.actionField].timestamp)}
              form={
                <Formik
                  initialValues={{ name: '' }}
                  onSubmit={() => action.onSubmit(id, powerShareEnableOpt[action.actionName].id)}
                >
                  {(props) => (
                    <Form className="flex-auto">
                      <SelectOption
                        options={outputChangeOptions}
                        selectedOption={powerShareEnableOpt[action.actionName]}
                        title="Output option"
                        name="disable-output-select"
                        onChange={(e) => onChangeOutputOption(e, action.actionName)}
                      />
                      <InteractiveButton
                        text={action.btnText ?? 'Update'}
                        submit
                        disabled={!powerShareEnableOpt}
                        isLoading={props.isSubmitting}
                      />
                    </Form>
                  )}
                </Formik>
              }
            />
          );
        })}
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  settings: state.controllers.controller_settings,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getSettings: dispatch.controllers.getControllerSettings,
  reset: dispatch.controllers.resetSettings,
});

export const PowerShare = connect(mapState, mapDispatch)(PowerShareView);
